import { parseCsvRecords } from './parseCsvRow';
import type { SisFieldKey, SisMapping } from './sisFieldMapping';
import { schoolYearEndForDate } from '../../core/schoolYear';

export type SisStudent = {
  studentId: string;
  altIds: string[];
  school: string;
  grade: string;
  serviceStartYear: number | null;
  yearsInEl: number | null;
  elStatus: string | null;
  programModel: string | null;
  homeLanguage: string | null;
  elTeacher: string | null;
  pronouns: string | null;
  originatingDistrict: string | null;
  exitDate: string | null;
  exitReason: string | null;
  newcomerFlag: boolean | null;
  duallyIdentified: boolean | null;
  alternateAccess: boolean | null;
};

export const ID_COLUMNS = ['state student id', 'state id', 'district student id', 'local student id', 'student number', 'student id'];

const COLUMNS = {
  school: ['school', 'school name', 'school assignment', 'site', 'site name', 'campus'],
  grade: ['grade', 'grade level'],
  serviceStartDate: ['el service start date', 'el service start', 'el start date', 'el entry date', 'date entered el program'],
  yearsInEl: ['years in el program', 'years in el', 'length of time in lep/ell program'],
  elStatus: ['el status', 'ell status', 'lep status'],
  programModel: ['program model'],
  homeLanguage: ['home language', 'native language'],
  elTeacher: ['el teacher', 'assigned el teacher', 'assigned teacher', 'teacher'],
  pronouns: ['pronouns'],
  originatingDistrict: ['originating district', 'el service start district'],
  exitDate: ['exit date', 'el exit date'],
  exitReason: ['exit reason', 'el exit reason'],
  newcomerFlag: ['newcomer', 'newcomer flag', 'intake flag'],
  duallyIdentified: ['iep', 'iep status', 'dually identified', 'dually identified flag'],
  alternateAccess: ['alternate access', 'alternate access flag', 'alt access', 'alternate access tester', 'wida alternate access tester'],
} as const;

type ColumnKey = keyof typeof COLUMNS;

const MAPPED_COLUMNS: ReadonlyArray<[SisFieldKey, ColumnKey]> = [
  ['school', 'school'],
  ['grade', 'grade'],
  ['el_status', 'elStatus'],
  ['program_model', 'programModel'],
  ['home_language', 'homeLanguage'],
  ['assigned_teacher', 'elTeacher'],
  ['pronouns', 'pronouns'],
  ['originating_district', 'originatingDistrict'],
  ['exit_date', 'exitDate'],
  ['exit_reason', 'exitReason'],
  ['dually_identified', 'duallyIdentified'],
  ['alternate_access', 'alternateAccess'],
  ['newcomer_flag', 'newcomerFlag'],
];

function applyMapping(indexes: Record<ColumnKey, number | null>, mapping: SisMapping): void {
  for (const [fieldKey, columnKey] of MAPPED_COLUMNS) {
    if (fieldKey === 'newcomer_flag' && mapping.newcomer_flag === undefined) continue;
    const index = mapping[fieldKey];
    indexes[columnKey] = index === undefined || index < 0 ? null : index;
  }
  const serviceIdx = mapping.el_service_start ?? null;
  indexes.serviceStartDate = serviceIdx;
  indexes.yearsInEl = serviceIdx;
}

type ResolvedColumns = {
  records: string[][];
  indexes: Record<ColumnKey, number | null>;
  idIndexes: number[];
};

function resolveColumns(csvText: string, mapping?: SisMapping): ResolvedColumns | null {
  const records = parseCsvRecords(csvText.replace(/^\uFEFF/, ''));
  const header = records[0];
  if (!header) return null;

  const headerIndex = new Map<string, number>();
  header.forEach((name, i) => {
    const key = name.trim().toLowerCase();
    if (!headerIndex.has(key)) headerIndex.set(key, i);
  });

  const columnFor = (key: ColumnKey): number | null => {
    for (const alias of COLUMNS[key]) {
      const idx = headerIndex.get(alias);
      if (idx != null) return idx;
    }
    return null;
  };
  const indexes = Object.fromEntries(
    (Object.keys(COLUMNS) as ColumnKey[]).map((key) => [key, columnFor(key)]),
  ) as Record<ColumnKey, number | null>;
  if (mapping) applyMapping(indexes, mapping);

  const aliasIdIndexes = ID_COLUMNS
    .map((alias) => headerIndex.get(alias))
    .filter((idx): idx is number => idx != null);
  const idIndexes = [
    ...new Set([
      ...(mapping?.student_id != null ? [mapping.student_id] : []),
      ...(mapping?.state_student_id != null ? [mapping.state_student_id] : []),
      ...aliasIdIndexes,
    ]),
  ];
  return { records, indexes, idIndexes };
}

export function sisLoadBearingColumnsResolved(csvText: string, mapping?: SisMapping): boolean {
  if (mapping) {
    return (
      resolveColumns(csvText, mapping) != null &&
      mapping.student_id != null &&
      mapping.el_service_start != null &&
      mapping.exit_date != null &&
      mapping.school != null
    );
  }
  const resolved = resolveColumns(csvText);
  if (!resolved || resolved.idIndexes.length === 0) return false;
  const { indexes } = resolved;
  return (
    indexes.school != null &&
    (indexes.serviceStartDate != null || indexes.yearsInEl != null) &&
    indexes.exitDate != null
  );
}

export function sisStudentCovered(student: SisStudent): boolean {
  return student.school !== '' && (student.serviceStartYear != null || student.yearsInEl != null);
}

export function countSisDataRows(csvText: string): number {
  return parseCsvRecords(csvText.replace(/^\uFEFF/, ''))
    .slice(1)
    .filter((row) => row.some((cell) => cell.trim() !== '')).length;
}

export function parseSisRoster(csvText: string, mapping?: SisMapping): SisStudent[] {
  return mergeSisRosters(parseSisRosterRows(csvText, mapping));
}

export function parseSisRosterRows(csvText: string, mapping?: SisMapping): SisStudent[] {
  const resolved = resolveColumns(csvText, mapping);
  if (!resolved) return [];
  const { records, indexes, idIndexes } = resolved;
  if (idIndexes.length === 0) return [];

  const students: SisStudent[] = [];
  for (const row of records.slice(1)) {
    const get = (key: ColumnKey): string => {
      const idx = indexes[key];
      return idx != null ? (row[idx] ?? '').trim() : '';
    };
    const ids = [...new Set(idIndexes.map((idx) => (row[idx] ?? '').trim()).filter((v) => v !== ''))];
    const studentId = ids[0];
    if (studentId == null) continue;
    students.push({
      studentId,
      altIds: ids.slice(1),
      school: get('school'),
      grade: get('grade'),
      serviceStartYear: schoolYearEndFromDate(get('serviceStartDate')),
      yearsInEl: toYearsInEl(get('yearsInEl')),
      elStatus: toText(get('elStatus')),
      programModel: toText(get('programModel')),
      homeLanguage: toText(get('homeLanguage')),
      elTeacher: toText(get('elTeacher')),
      pronouns: toText(get('pronouns')),
      originatingDistrict: toText(get('originatingDistrict')),
      exitDate: toText(get('exitDate')),
      exitReason: toText(get('exitReason')),
      newcomerFlag: toBool(get('newcomerFlag')),
      duallyIdentified: toBool(get('duallyIdentified')),
      alternateAccess: toBool(get('alternateAccess')),
    });
  }
  return students;
}

type FieldStamps = Partial<Record<keyof SisStudent, number>>;

const MERGEABLE_FIELDS = (student: SisStudent) =>
  (Object.keys(student) as (keyof SisStudent)[]).filter(
    (key) => key !== 'studentId' && key !== 'altIds',
  );

function stampsFor(student: SisStudent, position: number): FieldStamps {
  const stamps: FieldStamps = {};
  for (const key of MERGEABLE_FIELDS(student)) {
    const value = student[key];
    if (value !== null && value !== '') stamps[key] = position;
  }
  return stamps;
}

function mergeByFieldRecency(
  base: SisStudent,
  baseStamps: FieldStamps,
  next: SisStudent,
  nextStamps: FieldStamps,
  teachers: TeacherMerge,
): { record: SisStudent; stamps: FieldStamps } {
  const record = { ...base };
  const stamps: FieldStamps = { ...baseStamps };

  for (const key of MERGEABLE_FIELDS(next)) {
    const value = next[key];
    if (value === null || value === '') continue;
    const at = nextStamps[key] ?? -1;
    if (at >= (stamps[key] ?? -1)) {
      (record as Record<string, unknown>)[key] = value;
      stamps[key] = at;
    }
  }

  record.elTeacher =
    teachers === 'replace'
      ? ((nextStamps.elTeacher ?? -1) >= (baseStamps.elTeacher ?? -1)
          ? (next.elTeacher ?? base.elTeacher)
          : base.elTeacher)
      : mergeTeachers(base.elTeacher, next.elTeacher);
  if (record.elTeacher != null) {
    stamps.elTeacher = Math.max(baseStamps.elTeacher ?? -1, nextStamps.elTeacher ?? -1);
  }

  const baseIdentity = new Set([base.studentId, ...base.altIds]);
  const supersedesIdentity =
    next.studentId !== base.studentId && next.altIds.some((id) => baseIdentity.has(id));
  const primary = supersedesIdentity ? next.studentId : base.studentId;
  record.studentId = primary;
  record.altIds = [
    ...new Set([...base.altIds, base.studentId, next.studentId, ...next.altIds]),
  ].filter((id) => id !== primary && id !== '');
  return { record, stamps };
}

export function mergeSisRosters(
  students: SisStudent[],
  options: MergeSisOptions = {},
): SisStudent[] {
  const teachers = options.teachers ?? 'union';
  const records: (SisStudent | null)[] = [];
  const stamps: FieldStamps[] = [];
  const indexById = new Map<string, number>();

  students.forEach((student, position) => {
    const ids = [student.studentId, ...student.altIds];
    const matched = [
      ...new Set(ids.map((id) => indexById.get(id)).filter((i) => i != null)),
    ] as number[];

    if (matched.length === 0) {
      records.push(student);
      stamps.push(stampsFor(student, position));
      const idx = records.length - 1;
      for (const id of ids) indexById.set(id, idx);
      return;
    }

    const target = Math.min(...matched);
    const ordered = [...matched].sort((a, b) => a - b);

    let acc = records[ordered[0] as number] as SisStudent;
    let accStamps = stamps[ordered[0] as number] as FieldStamps;
    for (const other of ordered.slice(1)) {
      const folded = mergeByFieldRecency(
        acc,
        accStamps,
        records[other] as SisStudent,
        stamps[other] as FieldStamps,
        teachers,
      );
      acc = folded.record;
      accStamps = folded.stamps;
    }
    const withRow = mergeByFieldRecency(
      acc,
      accStamps,
      student,
      stampsFor(student, position),
      teachers,
    );

    for (const other of matched) {
      if (other === target) continue;
      const vacated = records[other] as SisStudent;
      for (const id of [vacated.studentId, ...vacated.altIds]) indexById.set(id, target);
      records[other] = null;
    }

    records[target] = withRow.record;
    stamps[target] = withRow.stamps;
    for (const id of [withRow.record.studentId, ...withRow.record.altIds]) {
      indexById.set(id, target);
    }
  });

  return records.filter((record): record is SisStudent => record != null);
}

const TEACHER_SEPARATORS = /[;+&/\n\r]+/;

export function parseTeachers(elTeacher: string | null): string[] {
  if (elTeacher == null) return [];
  const names = elTeacher
    .split(TEACHER_SEPARATORS)
    .map((name) => name.trim())
    .filter((name) => name !== '');
  return [...new Set(names)];
}

function mergeTeachers(base: string | null, next: string | null): string | null {
  const names = [...new Set([...parseTeachers(base), ...parseTeachers(next)])];
  return names.length === 0 ? null : names.join('; ');
}

export type TeacherMerge = 'union' | 'replace';

export type MergeSisOptions = { teachers?: TeacherMerge };

const MONTH_ABBREVIATIONS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

export function schoolYearEndFromDate(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  let year: number | null = null;
  let month: number | null = null;

  const bareYear = trimmed.match(/^(\d{4})$/);
  if (bareYear) {
    const y = Number(bareYear[1]);
    return y >= 1900 && y <= 2100 ? y + 1 : null;
  }

  // SIS exports commonly carry a time on a date column ("6/30/2026 0:00:00",
  // "2026-06-30T00:00:00"), and some spell the month ("30-Jun-2026"). The time
  // says nothing about the school year, so it is accepted and ignored. Mirrors
  // parseServiceStartEndYear on the server.
  const iso = trimmed.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?(?:[T ]\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?Z?)?$/);
  const dmy = trimmed.match(/^\d{1,2}-([A-Za-z]{3})-(\d{4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?)?$/);
  const us = trimmed.match(
    /^(\d{1,2})[/-]\d{1,2}[/-](\d{2}|\d{4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AaPp][Mm])?)?$/,
  );
  if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
  } else if (dmy) {
    year = Number(dmy[2]);
    month = MONTH_ABBREVIATIONS[dmy[1]!.toLowerCase()] ?? null;
  } else if (us) {
    month = Number(us[1]);
    year = Number(us[2]);
    if (us[2]!.length === 2) {
      year += year < 50 ? 2000 : 1900;
    }
  }
  if (year == null || month == null || year < 1900 || year > 2100 || month < 1 || month > 12) {
    return null;
  }
  return schoolYearEndForDate({ year, month, day: 1 });
}

function toText(value: string): string | null {
  return value === '' ? null : value;
}

function toInt(value: string): number | null {
  if (value === '') return null;
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

function toYearsInEl(value: string): number | null {
  const n = toInt(value);
  return n != null && n >= 0 && n < 100 ? n : null;
}

function toBool(value: string): boolean | null {
  switch (value.trim().toLowerCase()) {
    case 'y':
    case 'yes':
    case 'true':
    case 't':
    case '1':
      return true;
    case 'n':
    case 'no':
    case 'false':
    case 'f':
    case '0':
      return false;
    default:
      return null;
  }
}

const STORAGE_KEY = 'bamboo.sisRoster.v1';

type SisStore = Pick<Storage, 'getItem' | 'setItem'>;

function browserStore(): SisStore | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function saveSisRoster(students: SisStudent[], store: SisStore | null = browserStore()): void {
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(students));
  } catch {
    return;
  }
}

export function loadSisRoster(store: SisStore | null = browserStore()): SisStudent[] {
  if (!store) return [];
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SisStudent[]) : [];
  } catch {
    return [];
  }
}

const RESOLVED_IDS_KEY = 'bamboo.sisResolvedIds.v1';

export function loadSisResolvedIds(store: SisStore | null = browserStore()): Set<string> {
  if (!store) return new Set();
  try {
    const raw = store.getItem(RESOLVED_IDS_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? (parsed as string[]) : []);
  } catch {
    return new Set();
  }
}

export function addSisResolvedIds(ids: string[], store: SisStore | null = browserStore()): void {
  if (!store) return;
  const merged = loadSisResolvedIds(store);
  ids.forEach((id) => merged.add(id));
  try {
    store.setItem(RESOLVED_IDS_KEY, JSON.stringify([...merged]));
  } catch {
    return;
  }
}

export function clearSisResolvedIds(store: SisStore | null = browserStore()): void {
  if (!store) return;
  try {
    store.setItem(RESOLVED_IDS_KEY, JSON.stringify([]));
  } catch {
    return;
  }
}

const MAPPED_KEY = 'bamboo.sisRosterMapped.v1';

export function saveSisRosterMapped(mapped: boolean, store: SisStore | null = browserStore()): void {
  if (!store) return;
  try {
    store.setItem(MAPPED_KEY, JSON.stringify(mapped));
  } catch {
    return;
  }
}

export function loadSisRosterMapped(store: SisStore | null = browserStore()): boolean {
  if (!store) return false;
  try {
    return store.getItem(MAPPED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function hasSisRosterMappedFlag(store: SisStore | null = browserStore()): boolean {
  if (!store) return false;
  try {
    return store.getItem(MAPPED_KEY) != null;
  } catch {
    return false;
  }
}

export function findSisInRoster(candidateIds: string[], roster: SisStudent[]): SisStudent | null {
  const ids = candidateIds.map((id) => id.trim()).filter((id) => id !== '');
  for (const id of ids) {
    const found = roster.find((s) => s.studentId === id || s.altIds.includes(id));
    if (found) return found;
  }
  return null;
}

export function findSisStudent(
  candidateIds: string[],
  store: SisStore | null = browserStore(),
): SisStudent | null {
  return findSisInRoster(candidateIds, loadSisRoster(store));
}
